import { getCurrentUser, getCurrentUserFromStore } from '@lastbrand/fe-common/universal-auth'

import { getStorage } from '@src/utils/enhancedStorage'

import { EMPTY_OBJECT } from '../constants/app'

import isBrowser from './isBrowser'
import toSHA1 from './toSHA1'

/** localStorage key for the legacy D2C user cache. Written by `setUser` in
 * `src/quince-site-integrated/src/utils/auth.js`; read by `AuthUtil.isLoggedIn`,
 * `auth-config.holder.ts`, and the cross-domain logout broadcast gate.
 */
// @i18n-ignore -- legacy D2C auth cache key
export const GATSBY_USER_STORAGE_KEY = 'gatsbyUser'

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export default class AuthUtil {
  private static hashedEmail: string

  static getUserHashedEmail = async (): Promise<string | undefined> => {
    if (!isBrowser()) return
    if (AuthUtil.hashedEmail) return AuthUtil.hashedEmail

    try {
      const user = await getCurrentUser()
      const email = user?.attributes?.email
      if (email) {
        const hashedEmail = await toSHA1(email)
        AuthUtil.hashedEmail = hashedEmail
        return hashedEmail
      }
    } catch (error) {
      console.error('Error getting user hashed email:', error)
      // Reset cached value on error
      AuthUtil.hashedEmail = ''
    }
    return undefined
  }

  static getUser = (): { name?: string; email?: string } => {
    if (isBrowser()) {
      const userFromStore = getCurrentUserFromStore()
      const user = userFromStore ? userFromStore : EMPTY_OBJECT
      return user
    }
    return EMPTY_OBJECT
  }

  static isLoggedIn = (): boolean => {
    // For synchronous usage, check localStorage as fallback
    if (!isBrowser()) return false
    const user = getCurrentUserFromStore()

    if (!user) {
      let userFromLocalStorage = getStorage().getItem(GATSBY_USER_STORAGE_KEY, { isGlobal: true })
      // Check if localStorage user has a valid email too

      try {
        userFromLocalStorage =
          typeof userFromLocalStorage === 'string' ? JSON.parse(userFromLocalStorage) : userFromLocalStorage
      } catch (error) {
        console.error('Error parsing user from localStorage:', error)
      }

      return !!(
        userFromLocalStorage &&
        typeof userFromLocalStorage === 'object' &&
        'email' in userFromLocalStorage &&
        (userFromLocalStorage as any).email?.trim().length
      )
    }

    // Check if user has a valid email, not just if user exists
    return !!user?.email?.trim().length
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  static getUserSession = async () => {
    if (isBrowser() && window.getUserSession) {
      return window.getUserSession()
    }
  }
}
